import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSSE } from '../../contexts/SSEContext';
import { dashboardApi } from '../../lib/api';
import { formatDateTime, formatTime } from '../../lib/utils';
import Loader from '../../components/common/Loader';

interface DashboardStats {
    totalEmployees: number;
    activeEmployees: number;
    scheduledToday: number;
    onRestToday: number;
    todayEntries: number;
    todayExits: number;
    todayDelays: number;
    weeklyHours: number;
    monthlyHours: number;
}

interface RecentTimeEntry {
    id: string;
    type: 'IN' | 'OUT' | 'BREAK' | 'RESUME';
    timestamp: string;
    employee: {
        name: string;
        surname: string;
        dni: string;
    };
}

interface AbsentEmployee {
    id: string;
    name: string;
    surname: string;
    dni: string;
    scheduleStartTime: string;
    scheduleEndTime: string;
    startWindow: Date;
    noLateUntil: Date;
    timezone: string;
}

const DashboardEmpresaPage: React.FC = () => {
    const { user, company } = useAuth();
    const { addHandlers, removeHandlers } = useSSE();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [recentEntries, setRecentEntries] = useState<RecentTimeEntry[]>([]);
    const [absentEmployees, setAbsentEmployees] = useState<AbsentEmployee[]>([]);
    const [loading, setLoading] = useState(true);
    const [employeesWithoutSchedule, setEmployeesWithoutSchedule] = useState<any[]>([]);
    const [showScheduleAlert, setShowScheduleAlert] = useState(false);
    const [showEmployeesList, setShowEmployeesList] = useState(false);

    // Verificar si se debe mostrar el aviso de turnos
    const checkShowScheduleAlert = (employees: any[]) => {
        const today = new Date().toISOString().split('T')[0];
        const dismissedDate = localStorage.getItem('scheduleAlertDismissed');

        // Solo mostrar si no se descartó hoy y hay empleados sin turno
        if (dismissedDate !== today && employees.length > 0) {
            setShowScheduleAlert(true);
        } else {
            setShowScheduleAlert(false);
        }
    };

    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);

            // Cargar todas las consultas en paralelo
            const [statsResponse, recentResponse, absentResponse, noScheduleResponse] = await Promise.allSettled([
                dashboardApi.getDashboardStats(),
                dashboardApi.getRecentTimeEntries(10).catch(() => ({ success: false, data: [] })),
                dashboardApi.getAbsentEmployees().catch(() => ({ success: false, data: [] })),
                dashboardApi.getEmployeesWithoutScheduleToday().catch(() => ({ success: false, data: [] }))
            ]);

            if (statsResponse.status === 'fulfilled' && statsResponse.value.success && statsResponse.value.data) {
                const backendData = statsResponse.value.data as any;
                const mappedStats = {
                    totalEmployees: backendData.employees?.total || 0,
                    activeEmployees: backendData.employees?.activeToday || 0,
                    scheduledToday: backendData.employees?.scheduledToday || 0,
                    onRestToday: backendData.employees?.onRestToday || 0,
                    todayEntries: backendData.timeEntries?.today?.total || 0,
                    todayExits: backendData.timeEntries?.today?.outs || 0,
                    todayDelays: backendData.timeEntries?.today?.delays || 0,
                    weeklyHours: backendData.hours?.weekly || 0,
                    monthlyHours: backendData.hours?.monthly || 0,
                };
                                setStats(mappedStats);
            }

            if (recentResponse.status === 'fulfilled' && recentResponse.value.success && recentResponse.value.data) {
                setRecentEntries(recentResponse.value.data as any || []);
            } else {
                setRecentEntries([]);
            }

            if (absentResponse.status === 'fulfilled' && absentResponse.value.success && absentResponse.value.data) {
                setAbsentEmployees(absentResponse.value.data as any || []);
            } else {
                setAbsentEmployees([]);
            }

            if (noScheduleResponse.status === 'fulfilled' && noScheduleResponse.value.success && noScheduleResponse.value.data) {
                const employees = noScheduleResponse.value.data as any || [];
                setEmployeesWithoutSchedule(employees);
                checkShowScheduleAlert(employees);
            } else {
                setEmployeesWithoutSchedule([]);
            }
        } catch (error) {
            console.error('Error al cargar datos del dashboard:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAbsentEmployees = useCallback(async () => {
        try {
            const absentResponse = await dashboardApi.getAbsentEmployees();
                        if (absentResponse.success && absentResponse.data) {
                setAbsentEmployees(absentResponse.data as any || []);
            }
        } catch (error) {
                    }
    }, []);

    useEffect(() => {
        loadDashboardData();

        const handleTimeEntryCreated = async () => {
            // Solo recargar stats y recent entries, no empleados ausentes
            try {
                const [statsResponse, recentResponse] = await Promise.all([
                    dashboardApi.getDashboardStats(),
                    dashboardApi.getRecentTimeEntries(10).catch(() => ({ success: false, data: [] }))
                ]);

                if (statsResponse.success && statsResponse.data) {
                    const backendData = statsResponse.data as any;
                    const mappedStats = {
                        totalEmployees: backendData.employees?.total || 0,
                        activeEmployees: backendData.employees?.activeToday || 0,
                        scheduledToday: backendData.employees?.scheduledToday || 0,
                        onRestToday: backendData.employees?.onRestToday || 0,
                        todayEntries: backendData.timeEntries?.today?.total || 0,
                        todayExits: backendData.timeEntries?.today?.outs || 0,
                        todayDelays: backendData.timeEntries?.today?.delays || 0,
                        weeklyHours: backendData.hours?.weekly || 0,
                        monthlyHours: backendData.hours?.monthly || 0,
                    };
                    setStats(mappedStats);
                }

                if (recentResponse.success && recentResponse.data) {
                    setRecentEntries(recentResponse.data as any || []);
                }
            } catch (error) {
                console.error('Error al recargar datos de fichaje:', error);
            }
        };

        const handleNotificationCreated = async (data: any) => {
            if (data.notification?.type === 'ABSENCE_REQUEST') {
                await loadAbsentEmployees();
            }
        };

        const handleNotificationDeleted = async () => {
            await loadAbsentEmployees();
        };

        addHandlers({
            onTimeEntryCreated: handleTimeEntryCreated,
            onNotificationCreated: handleNotificationCreated,
            onNotificationDeleted: handleNotificationDeleted,
        });

        return () => {
            removeHandlers();
        };
    }, [addHandlers, removeHandlers, loadDashboardData, loadAbsentEmployees]);

    const getEntryTypeLabel = (type: string) => {
        switch (type) {
            case 'IN': return 'Entrada';
            case 'OUT': return 'Salida';
            case 'BREAK': return 'Pausa';
            case 'RESUME': return 'Reanudar';
            default: return type;
        }
    };

    const getEntryTypeColor = (type: string) => {
        switch (type) {
            case 'IN': return 'bg-green-100 text-green-800';
            case 'OUT': return 'bg-red-100 text-red-800';
            case 'BREAK': return 'bg-yellow-100 text-yellow-800';
            case 'RESUME': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader size="lg" text="Cargando dashboard..." />
            </div>
        );
    }

    return (
        <div className="space-y-6">
             {/* Header */}
             <div>
                 <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                 <p className="text-gray-600 dark:text-gray-400">
                     Bienvenido, {user?.name}. Aquí tienes un resumen de {company?.name}.
                 </p>
             </div>

             {/* Alerta de empleados sin turnos */}
              {showScheduleAlert && employeesWithoutSchedule.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                          <div className="flex items-start">
                              <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <div className="ml-3">
                                  <h3 className="text-lg font-semibold text-amber-900">
                                      Hay {employeesWithoutSchedule.length} {employeesWithoutSchedule.length === 1 ? 'empleado' : 'empleados'} sin turnos hoy
                                  </h3>
                                  <p className="mt-1 text-sm text-amber-800">
                                      Muchas de las funcionalidades y estadísticas no funcionarán correctamente si no tienen los turnos creados.
                                      Asegúrate de crear los turnos en la sección de Horarios.
                                  </p>
                                  {showEmployeesList && (
                                      <div className="mt-2 text-xs text-amber-700">
                                          <span className="font-medium">Empleados sin turno: </span>
                                          {employeesWithoutSchedule.map(e => `${e.name} ${e.surname || ''}`).join(', ')}
                                      </div>
                                  )}
                              </div>
                          </div>
                          <button
                              onClick={() => {
                                  const today = new Date().toISOString().split('T')[0];
                                  localStorage.setItem('scheduleAlertDismissed', today);
                                  setShowScheduleAlert(false);
                              }}
                              className="flex-shrink-0 text-amber-600 hover:text-amber-800"
                              title="No mostrar este mensaje hoy"
                          >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                          </button>
                      </div>
                      <button
                          onClick={() => setShowEmployeesList(!showEmployeesList)}
                          className="mt-3 text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center"
                      >
                          {showEmployeesList ? (
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                          ) : (
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                          )}
                          {showEmployeesList ? 'Ocultar' : 'Ver'} lista de empleados
                      </button>
                  </div>
              )}

            {/* Tarjetas de estadísticas */}
            {stats && (
                <div className="space-y-6">
                    {/* Primera fila */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Empleados</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalEmployees}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Programados Hoy</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.scheduledToday}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Activos Ahora</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeEmployees}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a9 9 0 00-9 9 9 9 0 0018 0" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Descanso Hoy</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.onRestToday}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Segunda fila */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Fichajes Hoy</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayEntries}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-amber-100 dark:bg-amber-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Retrasos Hoy</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayDelays}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-red-100 dark:bg-red-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Ausencias Ahora</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{absentEmployees.length}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3">
                                    <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Horas Semana</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{(stats.weeklyHours || 0).toFixed(1)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Fichajes recientes */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fichajes Recientes</h2>
                </div>
                <div className="p-6">
                    {recentEntries.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="text-gray-400 text-lg mb-2">📊</div>
                            <p className="text-gray-500 dark:text-gray-400">No hay fichajes recientes</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {recentEntries.map((entry) => (
                                <div key={entry.id} className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getEntryTypeColor(entry.type)}`}>
                                            {getEntryTypeLabel(entry.type)}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {entry.employee.name} {entry.employee.surname}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                DNI: {entry.employee.dni}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {formatTime(entry.timestamp)}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {formatDateTime(entry.timestamp)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Empleados ausentes ahora */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ausencias Ahora</h2>
                </div>
                <div className="p-6">
                    {absentEmployees.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="text-gray-400 text-lg mb-2">✅</div>
                            <p className="text-gray-500 dark:text-gray-400">No hay ausencias registradas en este momento</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {absentEmployees.map((employee) => (
                                <div key={employee.id} className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 px-2 py-1 rounded-full text-xs font-medium">
                                            Ausente
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {employee.name} {employee.surname}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                DNI: {employee.dni}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {employee.scheduleStartTime} - {employee.scheduleEndTime}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Turno programado
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardEmpresaPage;